// Historial operativo del envío, dentro del detalle: dónde está hoy (estado,
// estantería) y todo lo que le pasó (ingreso, estanteado, movido, salió,
// entregado), con quién y cuándo. Hasta ahora los eventos se guardaban y
// ninguna pantalla los mostraba.
import React, { useEffect, useState } from "react";
import { authFetch } from "../../../lib/authFetch";

type Evento = {
  id: number; from_status: string | null; to_status: string; source: string; actor: string | null; notes: string | null; at: string;
  route_id: number | null; route_type: string | null; route_date: string | null;
};
type Estado = {
  ops_status: string | null; ops_status_at: string | null; shelf_code: string | null; shelf_name: string | null; shelved_at: string | null;
  geo_status: string | null; lynx_code: string | null;
};

const ESTADO: Record<string, string> = {
  ENTERED: "ingresado", COLLECTED: "retirado en la tienda", ARRIVED_WAREHOUSE: "en planta", SHELVED: "en estantería",
  ASSIGNED_TO_ROUTE: "asignado a recorrido", OUT_FOR_DELIVERY: "en reparto", DELIVERED: "entregado", NOT_DELIVERED: "no entregado", CANCELLED: "cancelado",
};
const ORIGEN: Record<string, string> = {
  scanner: "escáner", driver_app: "app del chofer", admin: "panel", manual: "panel", store: "tienda", system: "sistema", retry: "reintento",
};
const RECORRIDO: Record<string, string> = { PICKUP: "colecta", DELIVERY: "reparto", TURBO: "turbo" };

const estado = (s: string | null | undefined) => (s ? ESTADO[s] ?? s : "—");
const fecha = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

const boxStyle: React.CSSProperties = { gridColumn: "1 / -1", border: "1px solid var(--border)", borderRadius: "16px", background: "var(--surface-alt)", padding: "14px" };
const labelStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "6px" };
const mutedStyle: React.CSSProperties = { color: "var(--text-secondary)", fontSize: "13px" };
const filaStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "110px minmax(0, 1fr)", gap: "10px", padding: "8px 0", borderTop: "1px solid var(--border)" };

export default function OpsHistoryPanel({ deliveryId, token }: { deliveryId: number; token: string | null }) {
  const [data, setData] = useState<{ delivery: Estado; events: Evento[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setData(null); setError(null);
    authFetch(`/v2/ops/deliveries/${deliveryId}/history`, { token })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok || j?.ok === false) throw new Error(j?.error || `Error ${r.status}`);
        return j;
      })
      .then((j) => { if (vivo) setData({ delivery: j.delivery, events: j.events ?? [] }); })
      .catch((e) => { if (vivo) setError(e instanceof Error ? e.message : String(e)); });
    return () => { vivo = false; };
  }, [deliveryId, token]);

  return (
    <div style={boxStyle}>
      <div style={labelStyle}>Historial operativo</div>
      {error ? <div style={{ color: "var(--danger)" }}>{error}</div> : null}
      {!data && !error ? <div style={mutedStyle}>Cargando…</div> : null}
      {data ? (
        <>
          <div style={{ color: "var(--text-primary)", marginBottom: "6px" }}>
            Hoy: <b>{estado(data.delivery.ops_status)}</b>
            {data.delivery.shelf_code ? <> en <b>{data.delivery.shelf_code}</b>{data.delivery.shelf_name ? ` (${data.delivery.shelf_name})` : ""}</> : null}
            {data.delivery.ops_status_at ? <span style={mutedStyle}> · desde {fecha(data.delivery.ops_status_at)}</span> : null}
            {data.delivery.geo_status === "pending" ? <span style={{ ...mutedStyle, color: "var(--warning, #d29922)" }}> · geolocalización pendiente</span> : null}
          </div>
          {data.events.length === 0 ? (
            <div style={mutedStyle}>Sin movimientos registrados todavía.</div>
          ) : (
            <div>
              {data.events.map((e) => (
                <div key={e.id} style={filaStyle}>
                  <div style={mutedStyle}>{fecha(e.at)}</div>
                  <div>
                    <div style={{ color: "var(--text-primary)" }}>
                      {e.from_status && e.from_status !== e.to_status ? <><span style={mutedStyle}>{estado(e.from_status)} → </span>{estado(e.to_status)}</> : estado(e.to_status)}
                      {e.notes ? <span style={mutedStyle}> · {e.notes}</span> : null}
                    </div>
                    <div style={mutedStyle}>
                      {e.actor ?? "—"} · {ORIGEN[e.source] ?? e.source}
                      {e.route_id ? ` · ${RECORRIDO[e.route_type ?? ""] ?? "recorrido"} #${e.route_id}${e.route_date ? ` del ${e.route_date.slice(8, 10)}/${e.route_date.slice(5, 7)}` : ""}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
